import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CiUnlock, CiLock } from "react-icons/ci";
import { IoDocumentText } from "react-icons/io5";
import ProfileNav from "../../components/ProfileNav";
import Pagination from "../../components/Pagination";
import DeclineOrgModal from "./DeclineOrgModal";
import {
  activateRequests,
  fetchInactiveRequests,
} from "../../services/organisation";

const InactiveRequests = () => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [showOptions, setShowOptions] = useState(null);
  const [total, setTotal] = useState();
  const [openModal, setOpenModal] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(100);
  const queryClient = useQueryClient();

  const getInactiveRequests = useCallback(async () => {
    try {
      const res = await fetchInactiveRequests(currentPage, itemsPerPage);
      setTotal(res.data.totalRecords);
      toast.success(res.message);
      return res;
    } catch (error) {
      if (error.response) {
        toast.error(error?.response?.data?.message);
      }
    }
  }, [currentPage, itemsPerPage]);

  const handleApprove = async (request) => {
    setShowOptions(null);
    try {
      const res = await activateRequests(request.id);
      toast.success(res.message);
      queryClient.invalidateQueries({ queryKey: ["getAllRequests"] });
    } catch (error) {
      if (error.response) {
        toast.error(error?.response?.data?.message);
      }
    }
  };

  const handleSvgClick = (userId, event) => {
    setShowOptions((prev) => (prev === userId ? null : userId));
  };

  const handleDeclineClick = (request) => {
    setSelectedUser(request);
    setShowOptions(null);
    setOpenModal(true);
  };

  const query = useQuery({
    queryKey: ["getAllRequests"],
    queryFn: getInactiveRequests,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 5 * 60 * 1000,
  });

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="h-screen">
      <article className="h-screen flex gap-4">
        <section className="w-[48%] flex-grow-[6] pt-4 ">
          <div className="flex items-center justify-between mb-16">
            <h1 className="text-2xl font-bold text-navy-blue">
              Inactive Requests
            </h1>
            <div>
              <ProfileNav />
            </div>
          </div>
          <div className="bg-white w-full">
            <div className="flex justify-end px-3">
              <Pagination
                totalItems={total}
                itemsPerPage={itemsPerPage}
                paginate={paginate}
                currentPage={currentPage}
              />
            </div>
            <div className="overflow-x-auto w-full">
              <table className="min-w-max w-full">
                <thead>
                  <tr className="text-left border-b-2">
                    <th className="px-3 border-r font-normal text-[12px] text-black-shade/40">
                      <p className="w-[54px]">Request Id</p>
                    </th>
                    <th className="px-3 border-r font-normal text-[12px] text-black-shade/40">
                      <p>Name</p>
                    </th>

                    <th className="px-3 py-3 font-normal text-[12px] border-r text-black-shade/40">
                      <p className="w-[54px]">Email Address</p>
                    </th>

                    <th className="px-3 border-r font-normal text-[12px] text-black-shade/40">
                      <p className="w-[54px]">Creation Date</p>
                    </th>
                    <th className="px-3 border-r font-normal text-[12px] text-black-shade/40">
                      <p className="w-[54px]">Updated Date</p>
                    </th>

                    <th className="px-3 border-r font-normal text-[12px] text-black-shade/40">
                      <p className="w-[54px]">Domain</p>
                    </th>
                    <th className="px-3 border-r font-normal text-[12px] text-black-shade/40">
                      <p className="w-[54px]">IsActive</p>
                    </th>
                    <th className="px-3 border-r font-normal text-[12px] text-black-shade/40">
                      <p className="w-[54px]">IsDefault</p>
                    </th>
                    <th className="px-3 border-r font-normal text-[12px] text-black-shade/40">
                      <p className="w-[54px]">Logo</p>
                    </th>
                    <th className="px-3 border-r font-normal text-[12px] text-black-shade/40">
                      <p className="w-[54px]">Primary Color</p>
                    </th>
                    <th className="px-3 border-r font-normal text-[12px] text-black-shade/40">
                      <p className="w-[54px]">Secondary Color</p>
                    </th>

                    <th></th>
                  </tr>
                </thead>
                <tbody className="cursor-pointer relative">
                  {query?.data?.data?.map((requests, index) => (
                    <tr
                      key={requests.Id}
                      className="bg-white border-b text-[12px] text-black-shade font-normal hover:bg-black-shade/5"
                    >
                      <td className="py-2 px-3">{requests?.id}</td>
                      <td className="py-2 px-3">{requests?.name}</td>

                      <td className="py-2 px-3">{requests?.email}</td>

                      <td className="py-2 px-3">
                        <div className="flex items-center gap-1">
                          <span>
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M10.5 2V1.5C10.5 1.22386 10.7239 1 11 1C11.2761 1 11.5 1.22386 11.5 1.5V2H13C13 2 13.4142 2 13.7071 2.29289C13.7071 2.29289 14 2.58579 14 3V13C14 13 14 13.4142 13.7071 13.7071C13.7071 13.7071 13.4142 14 13 14H3C3 14 2.58579 14 2.29289 13.7071C2.29289 13.7071 2 13.4142 2 13V3C2 3 2 2.58579 2.29289 2.29289C2.29289 2.29289 2.58579 2 3 2H4.5V1.5C4.5 1.22386 4.72386 1 5 1C5.27614 1 5.5 1.22386 5.5 1.5V2H10.5ZM3 6V13H13V6H3ZM13 5H3V3H4.5V3.5C4.5 3.77614 4.72386 4 5 4C5.27614 4 5.5 3.77614 5.5 3.5V3H10.5V3.5C10.5 3.77614 10.7239 4 11 4C11.2761 4 11.5 3.77614 11.5 3.5V3H13V5Z"
                                fill="#1C1C1C"
                                fill-opacity="0.4"
                              />
                            </svg>
                          </span>
                          <span className="">
                            {" "}
                            {new Date(requests.dateCreated).toLocaleDateString(
                              "en-US",
                              {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              }
                            )}
                          </span>
                        </div>
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-1">
                          <span>
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M10.5 2V1.5C10.5 1.22386 10.7239 1 11 1C11.2761 1 11.5 1.22386 11.5 1.5V2H13C13 2 13.4142 2 13.7071 2.29289C13.7071 2.29289 14 2.58579 14 3V13C14 13 14 13.4142 13.7071 13.7071C13.7071 13.7071 13.4142 14 13 14H3C3 14 2.58579 14 2.29289 13.7071C2.29289 13.7071 2 13.4142 2 13V3C2 3 2 2.58579 2.29289 2.29289C2.29289 2.29289 2.58579 2 3 2H4.5V1.5C4.5 1.22386 4.72386 1 5 1C5.27614 1 5.5 1.22386 5.5 1.5V2H10.5ZM3 6V13H13V6H3ZM13 5H3V3H4.5V3.5C4.5 3.77614 4.72386 4 5 4C5.27614 4 5.5 3.77614 5.5 3.5V3H10.5V3.5C10.5 3.77614 10.7239 4 11 4C11.2761 4 11.5 3.77614 11.5 3.5V3H13V5Z"
                                fill="#1C1C1C"
                                fill-opacity="0.4"
                              />
                            </svg>
                          </span>
                          <span className="">
                            {" "}
                            {new Date(requests.dateUpdated).toLocaleDateString(
                              "en-US",
                              {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              }
                            )}
                          </span>
                        </div>
                      </td>

                      <td className="py-2 px-3">{requests?.domain}</td>
                      <td className="py-2 px-3">
                        {requests?.isActive ? "true" : "false"}
                      </td>
                      <td className="py-2 px-3">
                        {requests?.isDefault ? "true" : "false"}
                      </td>
                      <td className="py-2 px-3">{requests?.logo}</td>
                      <td className="py-2 px-3">{requests?.primaryColor}</td>
                      <td className="py-2 px-3">{requests?.secondaryColor}</td>

                      {/* <section className="relative"> */}
                      <td className="relative">
                        <svg
                          className="mt-2"
                          width="20"
                          height="8"
                          viewBox="0 0 19 4"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          onClick={(event) =>
                            handleSvgClick(requests?.id, event)
                          }
                        >
                          <g opacity="0.3">
                            <path
                              d="M13.4874 0.512567C12.804 -0.170848 11.696 -0.170848 11.0126 0.512567C10.3291 1.19598 10.3291 2.30402 11.0126 2.98744C11.696 3.67085 12.804 3.67085 13.4874 2.98744C14.1709 2.30405 14.1709 1.19601 13.4874 0.512567Z"
                              fill="#030229"
                            />
                            <path
                              d="M8.23744 0.512567C7.55402 -0.170848 6.44598 -0.170848 5.76257 0.512567C5.07915 1.19598 5.07915 2.30402 5.76257 2.98744C6.44598 3.67085 7.55402 3.67085 8.23744 2.98744C8.92085 2.30405 8.92085 1.19601 8.23744 0.512567Z"
                              fill="#030229"
                            />
                            <path
                              d="M2.98744 0.512567C2.30402 -0.170848 1.19598 -0.170848 0.512564 0.512567C-0.170852 1.19598 -0.170852 2.30402 0.512564 2.98744C1.19598 3.67085 2.30402 3.67085 2.98744 2.98744C3.67085 2.30405 3.67085 1.19601 2.98744 0.512567Z"
                              fill="#030229"
                            />
                          </g>
                        </svg>

                        {/* EditDeleteModal within the same page */}
                        {showOptions === requests?.id && (
                          <div className="bg-white border absolute right-4 top-10 z-[2000] flex flex-col gap-3 items-center p-2 w-[110px] rounded-[10px] shadow-md">
                            <button
                              className="flex items-center gap-2 pl-2 w-[90px] h-[27px] text-[#5B93FF] rounded-[5px] font-[Nunito] text-xs bg-[#5B93FF]/15"
                              onClick={() => handleApprove(requests)}
                            >
                              <svg
                                width="8"
                                height="8"
                                viewBox="0 0 8 8"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  fill-rule="evenodd"
                                  clip-rule="evenodd"
                                  d="M2.65683 7.09496L6.31783 2.3606C6.51679 2.10529 6.58754 1.81012 6.52121 1.50958C6.46374 1.23636 6.29572 0.976572 6.04369 0.779493L5.42911 0.291277C4.89411 -0.134234 4.23088 -0.0894431 3.85063 0.398774L3.43944 0.932229C3.38638 0.998967 3.39964 1.09751 3.46597 1.15125C3.46597 1.15125 4.50501 1.98436 4.52712 2.00227C4.59787 2.06946 4.65092 2.15904 4.66419 2.26654C4.6863 2.47705 4.54039 2.67413 4.32373 2.70101C4.22204 2.71444 4.12477 2.68309 4.05402 2.62486L2.96192 1.75593C2.90886 1.71606 2.82927 1.72457 2.78506 1.77832L0.189641 5.13761C0.0216244 5.34813 -0.035855 5.62135 0.0216244 5.88561L0.353236 7.32339C0.370922 7.39953 0.437244 7.45328 0.516831 7.45328L1.97592 7.43537C2.24121 7.43089 2.48882 7.30995 2.65683 7.09496ZM4.69983 6.64712H7.07904C7.31116 6.64712 7.49996 6.83838 7.49996 7.07352C7.49996 7.30912 7.31116 7.49993 7.07904 7.49993H4.69983C4.4677 7.49993 4.27891 7.30912 4.27891 7.07352C4.27891 6.83838 4.4677 6.64712 4.69983 6.64712Z"
                                  fill="#5B93FF"
                                />
                              </svg>
                              Activate
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </article>
      {openModal && (
        <DeclineOrgModal
          isOpen={openModal}
          onClose={() => setOpenModal(false)}
          requests={selectedUser}
        />
      )}
    </div>
  );
};

export default InactiveRequests;
